// AssetPeek — THE one asset peek (`?peek=assets:<id>` via the shared PeekHost).
// FACE-BLIND across the register/books list faces since T (architect-ruled:
// projections of one record — the peek never reads `?view=`); the DEVICE FACE
// (the Devices entry) is the one earned exception, keyed on the HOST ROUTE per the peek-face
// law (a data-domain difference, one component, one collection key — the sites
// Registry/Solution mechanism transposed): opened from `/assets/devices` the
// panel LEADS with the connectivity story —
//   band → Reach (path · gateway as a sliding ref · address · SIMs) →
//   Services (doors + secret markers + the reveal for key holders) →
//   What & where (full) → Record/Findings — and Open-full-page commits to the
//   Connectivity tab. Every other host keeps the record order:
//   band (jump) → What & where → Condition & cover → Books (asset.books:read —
//   the full money set; depreciation + current value join here later) → Record.
// Observations follow the same law (the peek owns COMPLETENESS): the server's
// detail read sends register families to every reader and the books set only
// to books:read holders — the client renders what arrives, one worst-first
// list, no face filter (the list's per-view lens is a LIST rule).
//
// The acts come from the asset's ONE registry (`useAssetActions`, shared with
// the page — ADR-036 §7): the top row = Edit asset (the main button) + the Edit
// menu (Edit books · Edit connectivity · Device settings, disabled), on both
// faces. Deliberately absent until their slices: the footer Actions door (the
// asset's lifecycle — no dead menu items) and evidence/dismissals on observation
// cards (today's families are all completeness "missing" rows derived at read
// time).

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isBooksCategory, factState } from '@ems-app/schemas/assets/categories';
import { DEVICE_ACCESS_PRIMARY_SECRET, type DeviceAccessKind } from '@ems-app/schemas/assets/access';
import { useCan } from '../../hooks/useCan.js';
import { useAssetBooks, useAssetDetail, useAssetAccess, revealAssetSecret } from '../../hooks/useAssets.js';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { useAssetActions } from './AssetActions.js';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { EntityRef } from '../../components/patterns/EntityRef.js';
import {
  AssetObservationCards,
  AssetWorkBand,
  CATEGORY_ICON,
  LOCATION_KIND_ICON,
  STATUS_BADGE,
  assetObservationFamilies,
  productLineOf,
  emDash,
  refWords,
} from '../../components/patterns/assetsDisplay.js';
import {
  ConnectivityPathValue,
  RevealableSecret,
  SimSlotRows,
  serviceSplit,
} from '../../components/patterns/connectivityDisplay.js';
import { Badge } from '../../components/ui/Badge.js';
import { InlineCopy } from '../../components/ui/CopyField.js';
import { icons } from '../../components/ui/icons.js';
import { formatDate, formatMoney, formatNumber } from '../../lib/format.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';

export function AssetPeek({ projectId, assetId, onClose }: { projectId: string; assetId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation('assets');
  const lang = i18n.language;
  const can = useCan(projectId);
  const canBooks = can('asset.books:read');
  const { followPeek } = useEntityPeek();

  // The DEVICE FACE keys on the HOST ROUTE (the peek-face law — never a second
  // component or collection key): opened from the Devices entry, the panel
  // leads with the connectivity story.
  const isDeviceFace = useLocation().pathname.endsWith('/assets/devices');

  const detailQ = useAssetDetail(projectId, assetId);
  // The money detail (owner · mode · cost …) is the gated books facet — fetched
  // only for holders AND only where the category carries books of its own
  // (`booksDefault`): a module's value lives on its power system, so the facet
  // is structurally absent there, not empty.
  const hasBooks = detailQ.data ? isBooksCategory(detailQ.data.category) : false;
  // Fetched on EVERY face the viewer may read (the 2026-09-01 regression fix):
  // the device face defers the books SECTION, but the Edit menu's Edit books row
  // needs the data to seed its drawer — deferring the fetch silently removed
  // the row while completeness still showed the books gaps.
  const booksQ = useAssetBooks(projectId, assetId, { enabled: canBooks && hasBooks });
  // The device face's lead data — the address half (asset:read; F6).
  const accessQ = useAssetAccess(projectId, assetId, { enabled: isDeviceFace && detailQ.data?.network_capable === true });
  const canReveal = can('asset.private_fields:read');

  const recordRef = useRef<HTMLDivElement>(null);
  const jumpToRecord = () => recordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const a = detailQ.data;
  const b = booksQ.data;
  // The unit's clocks read where it stands (ADR-082 §2) — the same zone its overview tab shows.
  const display = useDisplayZone(a?.location?.timezone_iana ? { kind: 'site', projectId, zone: a.location.timezone_iana } : { kind: 'project', projectId });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  // The server already scoped the set to this viewer (books families arrive
  // only for books:read holders) — render what arrives, worst first.
  const families = a ? assetObservationFamilies(a) : [];

  // The registry's acts — nothing until the asset is there, and nothing for a viewer with no write key.
  const { acts, overlays } = useAssetActions(projectId, a, b);

  const CatIcon = a ? CATEGORY_ICON[a.category] : icons.asset;
  const eyebrow = a
    ? [t(`category.${a.category}`), a.model?.manufacturer ?? null].filter(Boolean).join(' · ')
    : '';
  const title = a
    ? `${a.model?.label ?? t('list.noModel')}${a.unit_count != null ? ` × ${formatNumber(a.unit_count)}` : ''}`
    : '…';
  const KindIcon = a?.location ? LOCATION_KIND_ICON[a.location.kind] : null;
  const locMarker = a?.is_unallocated ? ` · ${t('list.unallocated')}` : '';
  // Every link inside the sheet is FOLLOWED — the unit joins the way back by its reference (its title is its
  // product's name: "Back to" the product would read as the product just opened).
  const backLabel = a?.reference || title;
  const placeSite = a?.location?.can_open ? a.location.site_id : null;

  return (
    <>
    <ContextPanel
      host="peek"
      onClose={onClose}
      leading={<CatIcon size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
      kind={eyebrow}
      title={title}
      status={a ? <Badge variant={STATUS_BADGE[a.status]}>{t(`status.${a.status}`)}</Badge> : undefined}
      acts={acts}
      // The device face commits to the wires (the ruled landing — commits land on the wires); every other host to the record page.
      openFullPath={isDeviceFace ? `/p/${projectId}/assets/${assetId}/connectivity` : `/p/${projectId}/assets/${assetId}`}
    >
      {detailQ.isError ? (
        <p role="alert" className="text-sm text-[var(--color-danger)]">{t('peek.error')}</p>
      ) : !a ? (
        <p className="text-sm text-[var(--color-text-secondary)]">{t('common:statuses.loading')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          <AssetWorkBand families={families} onJump={jumpToRecord} t={t} />
          {/* ── THE DEVICE FACE's lead sections (the ruled face order): Reach → Services —
              summary grammar (the tab keeps the full cards one commit away).
              The gateway is a sliding ref (the porthole walks the chain). ── */}
          {isDeviceFace && a.network_capable && accessQ.data && (() => {
            const access = accessQ.data;
            const { used, available } = serviceSplit(access);
            const secretValue = (kind: DeviceAccessKind, entry: (typeof used)[number]['entry']) => {
              const primary = DEVICE_ACCESS_PRIMARY_SECRET[kind];
              if (primary) {
                const set = entry.secrets_set.includes(primary);
                return (
                  <RevealableSecret
                    set={set}
                    primaryMissing={!set}
                    onReveal={canReveal && set ? () => revealAssetSecret(projectId, assetId, kind, primary) : undefined}
                  />
                );
              }
              // Secretless doors (modbus · vpn): the one coordinate that names the dial.
              const v = entry.fields.unit_id ?? entry.fields.flavor ?? entry.fields.port;
              return v !== undefined && v !== null && v !== '' ? <span dir="ltr">{String(v)}</span> : emDash;
            };
            return (
              <>
                <DetailSection dense title={t('device.sections.reach')}>
                  <DescriptionList>
                    <Field label={t('device.path.label')}>
                      {access.access_path === 'via' && access.accessed_via ? (
                        <EntityRef
                          name={t('device.path.via', { name: access.accessed_via.label })}
                          onOpen={() => followPeek(`assets:${access.accessed_via!.id}`, backLabel)}
                        />
                      ) : (
                        <ConnectivityPathValue access={access} />
                      )}
                    </Field>
                    <Field label={access.access_path === 'via' ? t('device.fields.localIp') : t('device.fields.deviceIp')}>
                      {access.device_ip ? <InlineCopy value={access.device_ip} iconAlways /> : emDash}
                    </Field>
                    {((access.model?.fronts ?? false) || access.fronts.length > 0) && (
                      <Field label={t('device.fields.fronts')}>
                        {access.fronts.length === 0 ? (
                          <span className="text-[var(--color-text-secondary)]">{t('device.fronts.none')}</span>
                        ) : (
                          t('device.fronts.count', { count: access.fronts.length })
                        )}
                      </Field>
                    )}
                    <SimSlotRows access={access} t={t} />
                  </DescriptionList>
                </DetailSection>
                <DetailSection dense title={t('device.sections.services')}>
                  {used.length === 0 ? (
                    <p className="text-sm text-[var(--color-text-secondary)]">{t('device.noServices')}</p>
                  ) : (
                    <DescriptionList>
                      {used.map(({ kind, entry }) => (
                        <Field key={kind} label={t(`device.kinds.${kind}`)}>
                          {secretValue(kind, entry)}
                        </Field>
                      ))}
                    </DescriptionList>
                  )}
                  {available.length > 0 && (
                    <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                      {t('device.alsoAvailable')}: {available.map((k) => t(`device.kinds.${k}`)).join(' · ')}
                    </p>
                  )}
                </DetailSection>
              </>
            );
          })()}
          {/* ONE merged section (architect-ruled): state, place, then identity.
              NOT here (P rulings): Parent — the full page's; Model — the header. */}
          <DetailSection dense title={t('peek.sections.what')}>
            <DescriptionList>
              <Field label={t('list.cols.status')}>
                {t(`status.${a.status}`)}
                {a.status_on ? ` · ${t('peek.since', { date: formatDate(a.status_on, 'date') })}` : ''}
              </Field>
              <Field label={t('list.cols.location')}>
                <div className="text-end">
                  <div className="inline-flex max-w-full items-center gap-1.5">
                    {KindIcon && <KindIcon size={14} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />}
                    {/* ONE reference among facts — the blue chip where the server says the viewer may open the site
                        (the place itself, or a storage's host); a depot, a van, or a site they may not open stays a fact. */}
                    {a.location && placeSite ? (
                      <EntityRef name={`${a.location.label}${locMarker}`} to={`/p/${projectId}/sites/${placeSite}`} onPeek={() => followPeek(`sites:${placeSite}`, backLabel)} />
                    ) : (
                      <span className="truncate">{a.location ? `${a.location.label}${locMarker}` : t('list.noLocation')}</span>
                    )}
                  </div>
                  {a.location?.reference && (
                    <div className="text-xs text-[var(--color-text-secondary)]"><InlineCopy value={a.location.reference} iconAlways /></div>
                  )}
                  {a.location_on && a.location_on !== a.status_on && (
                    <div className="text-xs text-[var(--color-text-secondary)]">{t('peek.since', { date: formatDate(a.location_on, 'date') })}</div>
                  )}
                </div>
              </Field>
              {a.status === 'stocked' && a.reserved_for_site && (
                <Field label={t('peek.fields.reserved')}>{refWords(a.reserved_for_site, t)}</Field>
              )}
              {/* The PRODUCT as a sliding ref (both faces — the ruled uplift): the
                  porthole moves to the product peek (specs; datasheets —
                  ADR-068). Products are peek-only by the earned-tier law, so
                  OPEN mode. */}
              {a.model && (
                <Field label={t('peek.fields.product')}>
                  <EntityRef name={productLineOf(a) ?? a.model.label} onOpen={() => followPeek(`asset-models:${a.model!.id}`, backLabel)} />
                </Field>
              )}
              <Field label={t('peek.fields.reference')}>
                {a.reference ? <InlineCopy value={a.reference} iconAlways /> : emDash}
              </Field>
              {/* Hide-quiet-blanks (the charter's render matrix): a blank row
                  says nothing worth space — followed blanks already speak
                  through the observation cards below. */}
              {a.reference_alt && (
                <Field label={t('list.cols.refAlt')}>
                  <InlineCopy value={a.reference_alt} iconAlways />
                </Field>
              )}
              {a.serial_number && (
                <Field label={t('list.cols.serial')}>
                  <InlineCopy value={a.serial_number} iconAlways />
                </Field>
              )}
              {a.name && <Field label={t('peek.fields.name')}>{a.name}</Field>}
              {a.rating_value != null && a.rating_unit && (
                <Field label={t('list.cols.rating')}>
                  <span dir="ltr">{`${formatNumber(a.rating_value)} ${t(`ratingUnit.${a.rating_unit}`)}`}</span>
                </Field>
              )}
              {a.unit_count != null && (
                <Field label={t('list.cols.units')}><span dir="ltr">{formatNumber(a.unit_count)}</span></Field>
              )}
            </DescriptionList>
          </DetailSection>
          {/* The repair-or-replace triad: verdict · evidence · who still pays.
              (The device face defers it — the ruled four-section face; one
              slide or commit away.) */}
          {!isDeviceFace && (
          <DetailSection dense title={t('peek.sections.condition')}>
            <DescriptionList>
              {a.condition && (
                <Field label={t('filters.condition')}>
                  {`${t(`condition.${a.condition}`)}${a.condition_on ? ` · ${formatDate(a.condition_on, 'date')}` : ''}`}
                </Field>
              )}
              {/* SoH is the BATTERY categories' quantitative evidence (T ruling
                  — sohEligible on the descriptor); other categories hide the
                  field rather than parade an em-dash. */}
              {factState(a.category, 'soh') !== 'na' && a.state_of_health_pct != null && (
                <Field label={t('peek.fields.soh')}>
                  <span dir="ltr">{`${formatNumber(a.state_of_health_pct)} %${a.state_of_health_on ? ` · ${formatDate(a.state_of_health_on, 'date')}` : ''}`}</span>
                </Field>
              )}
              {a.warranty_end_on && (
                <Field label={t('filters.warranty')}>
                  {t(a.warranty_end_on < new Date().toISOString().slice(0, 10) ? 'peek.warrantyExpired' : 'peek.warrantyUntil', {
                    date: formatDate(a.warranty_end_on, 'date'),
                  })}
                </Field>
              )}
              {a.supplier && (
                <Field label={t('peek.fields.supplier')}>
                  <span dir="ltr">{a.supplier}</span>
                </Field>
              )}
            </DescriptionList>
          </DetailSection>
          )}
          {/* The BOOKS section — asset.books:read-gated AND category-gated, the
              FULL money set (depreciation method + current value join here
              later); the device face defers it like Condition. */}
          {!isDeviceFace && canBooks && hasBooks && b && (
            <DetailSection dense title={t('peek.sections.books')}>
              <DescriptionList>
                <Field label={t('list.cols.owner')}>{b.owner ? b.owner.name : emDash}</Field>
                <Field label={t('filters.acquisitionMode')}>{b.acquisition_mode ? t(`acquisitionMode.${b.acquisition_mode}`) : emDash}</Field>
                <Field label={t('list.cols.acquiredOn')}>{b.acquired_on ? formatDate(b.acquired_on, 'date') : emDash}</Field>
                <Field label={t('list.cols.cost')}>
                  {b.acquisition_cost != null && b.acquisition_currency ? <span dir="ltr">{formatMoney(b.acquisition_cost, b.acquisition_currency)}</span> : emDash}
                </Field>
                <Field label={t('filters.bookStatus')}>{t(`bookStatus.${b.book_status}`)}</Field>
                {b.written_off_on && <Field label={t('peek.fields.writtenOff')}>{formatDate(b.written_off_on, 'date')}</Field>}
                {b.disposed_on && <Field label={t('peek.fields.disposed')}>{formatDate(b.disposed_on, 'date')}</Field>}
              </DescriptionList>
            </DetailSection>
          )}
          {/* THE FOOT — the record's own state: the stamp, then every
              observation card worst-first (or the one quiet complete line). */}
          <div ref={recordRef}>
            <DetailSection dense title={t('peek.sections.record')}>
              <DescriptionList>
                <Field label={t('peek.fields.created')}>{formatDate(a.created_at, 'date', { timeZone: zone })}</Field>
              </DescriptionList>
              <div className="mt-2.5">
                <AssetObservationCards families={families} lang={lang} t={t} />
              </div>
            </DetailSection>
          </div>
        </div>
      )}
    </ContextPanel>
    {overlays}
    </>
  );
}
